import React from 'react';
import { IconButton, ListItem, ListItemIcon, ListItemProps, Paper, styled, Tooltip } from '@mui/material';
import { Draggable } from '@hello-pangea/dnd';
import { Delete, DragHandle, Lock } from '@mui/icons-material';
import { FieldArrayWithId } from 'react-hook-form';
import { AddCustomPropertyInput, EditCustomPropertyInput } from 'gql';
import { useIntl } from 'react-intl';


interface OwnProps {
  option: FieldArrayWithId<AddCustomPropertyInput | Omit<EditCustomPropertyInput, 'id'>, 'customOptions', 'uniqueId'>;
  index: number;
  children?: React.ReactNode;
  onDelete: () => void;
  divider?: boolean;
  isSystemOption?: boolean;
}

const StyledListItem = styled(ListItem)<ListItemProps>(({ theme }) => ({
  paddingRight: theme.spacing(2),
  '& .MuiListItemIcon-root:first-of-type': {
    minWidth: '24px',
    marginRight: theme.spacing(2)
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  [theme.breakpoints.up('lg')]: {
    '&:hover .MuiIconButton-root': {
      visibility: 'visible'
    },
    '& .MuiIconButton-root': {
      visibility: 'hidden'
    },
  }
}));


export const DraggableOptionListItem: React.FC<OwnProps> = ({ option, index, children, divider, onDelete, isSystemOption }) => {
  const { formatMessage } = useIntl();

  return (
    <Draggable draggableId={option.uniqueId} index={index} isDragDisabled={isSystemOption}>
      {(provided, snapshot) => (
        <Paper square
          elevation={16}
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{ boxShadow: 'none' }}
        >
          <StyledListItem
            divider={divider}
            sx={{
              backgroundColor: snapshot.isDragging ? 'action.hover' : undefined
            }}
          >
            <ListItemIcon {...provided.dragHandleProps}>
              <DragHandle />
            </ListItemIcon>
            {children}
            {isSystemOption ? (
              <Tooltip title={formatMessage({ id: 'System option cannot be deleted' })}>
                <Lock color='disabled' sx={{ ml: 1 }} />
              </Tooltip>
            ) : (
              <IconButton edge='end' onClick={onDelete} sx={{ ml: 1 }}>
                <Delete color='error' />
              </IconButton>
            )}
          </StyledListItem>
        </Paper>
      )}
    </Draggable>
  );
};